import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api.js';
import { Badge, Card, ErrorNote, PageTitle, Spinner } from '../components/ui.jsx';

function Stat({ label, value, to, tone }) {
  const body = (
    <Card className="p-5 transition-transform hover:-translate-y-0.5">
      <p className="text-xs font-bold uppercase tracking-widest text-muted">{label}</p>
      <p className="mt-2 font-display text-4xl">{value}</p>
      {tone && (
        <p className="mt-2">
          <Badge tone={tone}>{tone}</Badge>
        </p>
      )}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    Promise.all([api.get('/api/admin/overview'), api.get('/api/health')])
      .then(([overview, healthData]) => {
        setData(overview);
        setHealth(healthData);
      })
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  if (error) return <ErrorNote error={error} onRetry={load} />;
  if (!data) return <Spinner />;

  const { counts } = data;

  return (
    <>
      <PageTitle
        title="Dashboard"
        description="Everything on the site at a glance. Publishing anywhere in the admin updates the live site within a couple of seconds."
      />

      <section>
        <h2 className="mb-4 text-xl">Content</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Case studies" value={counts.caseStudies} to="/content/work" />
          <Stat label="Services" value={counts.services} to="/content/services" />
          <Stat label="Media" value={counts.media} to="/media" />
          <Stat label="New leads" value={counts.newLeads} to="/leads" />
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-4 text-xl">System</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">API</p>
            <p className="mt-2">
              <Badge tone={health?.status === 'ok' ? 'PUBLISHED' : 'SPAM'}>
                {health?.status ?? '—'}
              </Badge>
            </p>
            <p className="mt-2 text-xs text-muted">{health?.environment}</p>
          </Card>

          <Card className="p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Database</p>
            <p className="mt-2">
              <Badge tone={health?.checks?.database?.status === 'ok' ? 'PUBLISHED' : 'SPAM'}>
                {health?.checks?.database?.status ?? '—'}
              </Badge>
            </p>
            <p className="mt-2 text-xs text-muted">
              {health?.checks?.database?.latencyMs}ms round trip
            </p>
          </Card>

          <Card className="p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Cache</p>
            <p className="mt-2">
              <Badge tone="PUBLISHED">{health?.checks?.cache?.driver ?? '—'}</Badge>
            </p>
            <p className="mt-2 text-xs text-muted">
              {health?.checks?.cache?.size ?? 0} entries ·{' '}
              {((health?.checks?.cache?.hitRate ?? 0) * 100).toFixed(0)}% hit rate
            </p>
          </Card>

          <Card className="p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">
              Content version
            </p>
            <p className="mt-2 font-display text-4xl">{data.contentVersion}</p>
            <p className="mt-2 text-xs text-muted">Increments on every publish</p>
          </Card>
        </div>
      </section>
    </>
  );
}
